import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useHelpSearch, useGroupedSearchResults } from "@/hooks/useHelpSearch";
import { useHelpCategories } from "@/hooks/useHelpCenter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Rocket,
  User,
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  MessageSquare,
  Shield,
  HelpCircle,
  Search,
  ChevronRight,
  ArrowLeft,
  FileText,
} from "lucide-react";

const iconMap: Record<string, React.ElementType> = {
  Rocket,
  User,
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  MessageSquare,
  Shield,
  HelpCircle,
};

// Highlight matching text in a string
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query || !text) return <>{text}</>;
  
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  
  return (
    <>
      {parts.map((part, i) => 
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-primary/20 text-foreground px-0.5 rounded">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export function SearchResults() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  
  const { data: results, isLoading } = useHelpSearch(query);
  const { data: categories } = useHelpCategories();
  const groupedResults = useGroupedSearchResults(results || []);
  
  const activeCategories = categories?.filter(c => c.is_active);
  
  const handleSearchChange = (newQuery: string) => {
    setSearchParams(newQuery ? { q: newQuery } : {});
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate("/help")}
          className="gap-2 -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Help Center
        </Button>
        
        <h1 className="text-2xl font-bold">Search Help Articles</h1>
        
        {/* Search Input */}
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search help articles..."
            value={query}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && query.length >= 2 && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {/* Results */}
      {!isLoading && query.length >= 2 && (
        <>
          {results && results.length > 0 ? (
            <div className="space-y-8">
              <p className="text-sm text-muted-foreground">
                Found {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
              </p>
              
              {/* Grouped Results by Category */}
              {groupedResults.map(group => {
                const Icon = iconMap[group.category?.icon || 'HelpCircle'] || HelpCircle;
                
                return (
                  <div key={group.category?.id || 'uncategorized'} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-primary/10">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <h3 className="font-semibold">{group.category?.name || "Uncategorized"}</h3>
                      <Badge variant="secondary" className="text-xs">
                        {group.results.length}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 pl-8">
                      {group.results.map(result => (
                        <Card
                          key={result.article.id}
                          className="cursor-pointer transition-all duration-200 hover:bg-muted/50 hover:border-primary/30 hover:shadow-sm"
                          onClick={() => navigate(`/help/article/${result.article.id}`)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-foreground mb-1">
                                  <HighlightedText text={result.article.title} query={query} />
                                </h4>
                                {result.snippet && (
                                  <p className="text-sm text-muted-foreground line-clamp-2">
                                    <HighlightedText text={result.snippet} query={query} />
                                  </p>
                                )}
                                <Badge variant="outline" className="mt-2 text-xs">
                                  {result.matchType === "title" ? "Title match" : 
                                   result.matchType === "summary" ? "Summary match" : "Content match"}
                                </Badge>
                              </div>
                              <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* No Results */
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Results Found</h3>
                <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                  We couldn't find any articles matching "{query}". Try different keywords or browse by category.
                </p>
                
                {/* Suggested Categories */}
                {activeCategories && activeCategories.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Browse Categories</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {activeCategories.slice(0, 5).map(cat => {
                        const CatIcon = iconMap[cat.icon || 'HelpCircle'] || HelpCircle;
                        return (
                          <Button
                            key={cat.id}
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => navigate(`/help/category/${cat.id}`)}
                          >
                            <CatIcon className="h-4 w-4" />
                            {cat.name}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Initial State (no query or too short) */}
      {query.length < 2 && (
        <div className="text-center py-8">
          <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            Enter at least 2 characters to search
          </p>
        </div>
      )}
    </div>
  );
}

export default SearchResults;
